import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type * as GeoJSON from "geojson";

import { api } from "../utils/api";
import { getApiBase } from "../utils/apiBase";
import { ApiResponse, ApiError } from "../types/api";
import {
  PublicStory,
  SceneTiles,
  StoryContent,
  StoryMap,
  StoryMapData,
  StoryMapSummary,
  StoryMapVisibility,
} from "../types/storyMap";
import {
  ConfidenceArrays,
  FrameArrays,
  RasterBounds,
  UrbanAnimationFields,
} from "../types/FireSimulation";
import {
  confidenceArraysFromPack,
  fetchFramePack,
  frameArraysFromPack,
  pickUrban,
} from "./simulations";
import { CustomLayer } from "../types/customLayer";
import { IgnitionRegime } from "../types/ignitionRegime";
import { b64ToF32, b64ToU8 } from "../utils/decodeFloat32";
import { decodeFramePack, FramePack, isFramePackUrl } from "../utils/framePack";

/**
 * Every story query shares this prefix — the per-project lists, the
 * organisation-wide list and the by-id rows — so a mutation invalidates them
 * all at once rather than guessing which list a story appears in.
 */
export const STORY_MAPS_KEY = ["storyMaps"] as const;
const storyMapsKey = (projectId: string | undefined) => [...STORY_MAPS_KEY, projectId];
export const ALL_STORY_MAPS_KEY = [...STORY_MAPS_KEY, "all"] as const;
const storyByIdKey = (storyMapId: string | undefined) => [...STORY_MAPS_KEY, "byId", storyMapId];

/** Stories are addressed by id alone; folder and organisation-wide stories have no project URL. */
const storyPath = (storyMapId: string | undefined, suffix = "") => `/api/story-maps/${storyMapId}${suffix}`;

export const useStoryMaps = (projectId: string | undefined) => {
  return useQuery<ApiResponse<StoryMapSummary[]>, ApiError>({
    queryKey: storyMapsKey(projectId),
    queryFn: () =>
      api.request<ApiResponse<StoryMapSummary[]>>(
        `/api/projects/${projectId}/story-maps`
      ),
    enabled: !!projectId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Every story map the organisation has — project, folder and
 * organisation-wide — for the listing page's folder browser. Fetched once and
 * filed into folders client-side, like the projects themselves.
 */
export const useAllStoryMaps = (enabled = true) => {
  return useQuery<ApiResponse<StoryMapSummary[]>, ApiError>({
    queryKey: ALL_STORY_MAPS_KEY,
    // Archived projects' stories are not served: they come back with the project.
    queryFn: () => api.request<ApiResponse<StoryMapSummary[]>>("/api/story-maps"),
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useStoryMap = (storyMapId: string | undefined) => {
  return useQuery<ApiResponse<StoryMap>, ApiError>({
    queryKey: storyByIdKey(storyMapId),
    queryFn: () => api.request<ApiResponse<StoryMap>>(storyPath(storyMapId)),
    enabled: !!storyMapId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Poll a story while it is PUBLISHING.
 *
 * Publishing runs as a backend task (it loads whole ensemble artifacts out of
 * S3), so POST /publish returns 202 and this follows the row to PUBLISHED —
 * the slug and the frozen content only exist once the task has finished.
 */
export const useStoryMapPublishStatus = (
  storyMapId: string | undefined,
  enabled: boolean
) => {
  return useQuery<ApiResponse<StoryMap>, ApiError>({
    queryKey: [...storyByIdKey(storyMapId), "publish-status"],
    queryFn: () => api.request<ApiResponse<StoryMap>>(storyPath(storyMapId)),
    enabled: enabled && !!storyMapId,
    refetchInterval: (query) =>
      query.state.data?.data.status === "PUBLISHING" ? 2000 : false,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
};

export interface CreateStoryMapPayload {
  title: string;
  content?: StoryContent;
}

/**
 * Where a new story belongs: one project, one folder (it may then draw from
 * every active project beneath that folder), or neither — the whole
 * organisation.
 */
export interface CreateStoryMapScopedPayload extends CreateStoryMapPayload {
  project_id?: number | string | null;
  folder_id?: number | null;
}

export const useCreateStoryMapScoped = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<StoryMap>, ApiError, CreateStoryMapScopedPayload>({
    mutationFn: ({ project_id, folder_id, ...rest }) =>
      api.request<ApiResponse<StoryMap>>("/api/story-maps", {
        method: "POST",
        body: {
          ...rest,
          ...(project_id != null ? { project_id: Number(project_id) } : {}),
          ...(folder_id != null ? { folder_id } : {}),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORY_MAPS_KEY });
    },
  });
};

/** A project story (the per-project Story Maps page). */
export const useCreateStoryMap = (projectId: string | undefined) => {
  const scoped = useCreateStoryMapScoped();
  return useMutation<ApiResponse<StoryMap>, ApiError, CreateStoryMapPayload>({
    mutationFn: (payload) => scoped.mutateAsync({ ...payload, project_id: projectId }),
  });
};

export interface UpdateStoryMapPayload {
  storyMapId: string;
  data: {
    title?: string;
    content?: StoryContent;
    visibility?: StoryMapVisibility;
    comments_enabled?: boolean;
  };
}

/** Plain PATCH; the builder's debounced autosave lives in the page, not here. */
export const useUpdateStoryMap = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<StoryMap>, ApiError, UpdateStoryMapPayload>({
    mutationFn: ({ storyMapId, data }) =>
      api.request<ApiResponse<StoryMap>>(storyPath(storyMapId), { method: "PATCH", body: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORY_MAPS_KEY });
    },
  });
};

export const useDeleteStoryMap = () => {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (storyMapId) => api.request<void>(storyPath(storyMapId), { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORY_MAPS_KEY });
    },
  });
};

export const usePublishStoryMap = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ApiResponse<StoryMap>,
    ApiError,
    { storyMapId: string; visibility?: StoryMapVisibility; commentsEnabled?: boolean }
  >({
    mutationFn: ({ storyMapId, visibility, commentsEnabled }) =>
      api.request<ApiResponse<StoryMap>>(storyPath(storyMapId, "/publish"), {
        method: "POST",
        body: {
          ...(visibility ? { visibility } : {}),
          ...(commentsEnabled !== undefined ? { comments_enabled: commentsEnabled } : {}),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORY_MAPS_KEY });
    },
  });
};

/**
 * Upload the PDF rendered for a story at publish time.
 *
 * Multipart rather than a base64 data URL: a story PDF carries a captured map
 * image per scene, and base64 in a JSON body would inflate it by a third and
 * hold the whole document in an API worker's memory.
 */
export const useUploadStoryPdf = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ApiResponse<{ url: string }>,
    ApiError,
    { storyMapId: string; pdf: Blob }
  >({
    mutationFn: async ({ storyMapId, pdf }) => {
      const form = new FormData();
      form.append("pdf", pdf, "story.pdf");
      return api.request<ApiResponse<{ url: string }>>(storyPath(storyMapId, "/pdf"), {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORY_MAPS_KEY });
    },
  });
};

/**
 * Attach the captured per-scene map images to a published story.
 *
 * Embedded views render these flat images rather than instantiating a Mapbox
 * GL map per reader — an embed on a busy page was otherwise unbounded,
 * unauthenticated billing on our access token.
 */
export const useAttachStorySceneImages = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ApiResponse<{ images: Record<string, string> }>,
    ApiError,
    { storyMapId: string; images: Record<string, string> }
  >({
    mutationFn: ({ storyMapId, images }) =>
      api.request<ApiResponse<{ images: Record<string, string> }>>(
        storyPath(storyMapId, "/scene-images"),
        { method: "POST", body: { images } }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORY_MAPS_KEY });
    },
  });
};

export const useUnpublishStoryMap = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<StoryMap>, ApiError, string>({
    mutationFn: (storyMapId) =>
      api.request<ApiResponse<StoryMap>>(storyPath(storyMapId, "/unpublish"), {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORY_MAPS_KEY });
    },
  });
};

/**
 * Public story fetch (no login required). Plain fetch rather than the shared
 * api wrapper so an anonymous 401/404 never triggers the global logout, but
 * credentials are included so ORG-visibility stories work for signed-in users.
 */
export const usePublicStory = (slug: string | undefined) => {
  return useQuery<PublicStory, ApiError>({
    queryKey: ["publicStory", slug],
    queryFn: async () => {
      const response = await fetch(
        `${getApiBase()}/api/story-maps/shared/${slug}`,
        { credentials: "include" }
      );
      if (!response.ok) {
        throw {
          message:
            response.status === 404
              ? "This story is not available."
              : "Failed to load story.",
          status: response.status,
        } as ApiError;
      }
      const json: ApiResponse<PublicStory> = await response.json();
      return json.data;
    },
    enabled: !!slug,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * The frozen geometry bundle for a published story.
 *
 * Publishing writes boundary/fire-history/treatment geometry to a public S3
 * object and stores its URL, rather than inlining a potentially multi-megabyte
 * blob into the story row and serving it to every reader in one response.
 * Stories published before that change carry `mapData` inline — those are
 * returned as-is, with no fetch.
 */
export const useFrozenMapData = (content: StoryContent | null | undefined) => {
  const inline = content?.mapData ?? null;
  const url = content?.mapDataUrl;

  const query = useQuery<StoryMapData, ApiError>({
    queryKey: ["storyMapData", url],
    queryFn: async () => {
      const response = await fetch(url as string);
      if (!response.ok) {
        throw { message: "Failed to load map data.", status: response.status } as ApiError;
      }
      return (await response.json()) as StoryMapData;
    },
    enabled: !inline && !!url,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    mapData: inline ?? query.data ?? null,
    /** True once there is nothing left to wait for (the print pipeline gates on this). */
    settled: !!inline || !url || !query.isPending,
  };
};

/**
 * Best-effort treatments FeatureCollection for a scene's strategy context,
 * used by the builder/preview map (published stories carry frozen treatments
 * in published_content.mapData instead).
 */
export const useStrategyTreatments = (
  projectId: string | undefined,
  strategyId: number | null | undefined,
  enabled = true
) => {
  return useQuery<GeoJSON.FeatureCollection | null, ApiError>({
    queryKey: ["storyMapTreatments", projectId, strategyId],
    queryFn: async () => {
      try {
        const response = await api.request<ApiResponse<GeoJSON.FeatureCollection>>(
          `/api/projects/${projectId}/mitigationStrategies/${strategyId}/treatments`
        );
        return response.data ?? null;
      } catch {
        return null;
      }
    },
    enabled: !!projectId && strategyId != null && enabled,
    refetchOnWindowFocus: false,
  });
};

// ---------------------------------------------------------------------------
// v2: fuel/veg tiles per (strategy, date), assets tiles, fire history and
// sim-animation frame arrays for story scenes/blocks.
// ---------------------------------------------------------------------------

type StrategyTilesState = "queued" | "running" | "succeeded" | "failed";

interface StrategyTilesStatusPayload {
  jobKey: string;
  code: number;
  message: string;
  state: StrategyTilesState;
  result?: Record<
    string,
    { tiles_url?: string | null; vegetation_tiles_url?: string | null }
  > | null;
}

interface StrategyTilesJobResponse {
  jobKey: string;
  status?: StrategyTilesStatusPayload;
}

export interface UseStrategyTilesResult {
  /** Resolved permanent public tile URLs, or null while unresolved. */
  tiles: SceneTiles | null;
  isResolving: boolean;
  error: ApiError | Error | null;
}

/**
 * Resolve the fuel/vegetation tilesets for a (strategy, date) pair via
 * strategyForYear: submit once, then poll .../status every ~2s while the job
 * is queued/running. Self-contained (modelled on StrategyContext) so story
 * pages don't depend on the project-page contexts.
 */
export const useStrategyTiles = (
  projectId: string | undefined,
  strategyId: number | null | undefined,
  date: string | null | undefined,
  enabled = true
): UseStrategyTilesResult => {
  const active = !!projectId && strategyId != null && !!date && enabled;

  const submitQuery = useQuery<ApiResponse<StrategyTilesJobResponse>, ApiError>({
    queryKey: ["storyStrategyTiles", projectId, strategyId, date],
    enabled: active,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      api.request<ApiResponse<StrategyTilesJobResponse>>(
        `/api/projects/${projectId}/strategyForYear?queryDate=${date}&mitigationStrategyId=${strategyId}`
      ),
  });

  const jobKey = submitQuery.data?.data?.jobKey ?? null;
  const submitStatus = submitQuery.data?.data?.status ?? null;
  // Skip polling when the submit response already carries a terminal status
  // (cached result on the backend).
  const submitTerminal =
    submitStatus?.state === "succeeded" || submitStatus?.state === "failed";

  const statusQuery = useQuery<ApiResponse<StrategyTilesStatusPayload>, ApiError>({
    queryKey: ["storyStrategyTilesStatus", jobKey],
    enabled: active && !!jobKey && !submitTerminal,
    refetchOnWindowFocus: false,
    queryFn: () =>
      api.post<ApiResponse<StrategyTilesStatusPayload>>(
        `/api/projects/${projectId}/strategyForYear/status`,
        { jobKey }
      ),
    refetchInterval: (query) => {
      const state = (
        query.state.data as ApiResponse<StrategyTilesStatusPayload> | undefined
      )?.data?.state;
      return state === "running" || state === "queued" ? 2000 : false;
    },
  });

  const latest = statusQuery.data?.data ?? submitStatus;
  const entry =
    strategyId != null ? latest?.result?.[String(strategyId)] ?? null : null;
  const tiles: SceneTiles | null = entry
    ? {
        fuelTilesUrl: entry.tiles_url ?? undefined,
        vegetationTilesUrl: entry.vegetation_tiles_url ?? undefined,
      }
    : null;

  const failed = latest?.state === "failed";
  // Terminal success without an entry for this strategy would otherwise spin
  // forever — surface it as an error instead.
  const succeededWithoutTiles = latest?.state === "succeeded" && !entry;
  const requestError = submitQuery.error ?? statusQuery.error ?? null;
  const error: ApiError | Error | null = requestError
    ? requestError
    : failed
    ? new Error(latest?.message || "Fuel state calculation failed")
    : succeededWithoutTiles
    ? new Error("No fuel state available for this strategy and date")
    : null;

  return {
    tiles: active ? tiles : null,
    isResolving: active && !tiles && !error,
    error: active ? error : null,
  };
};

/** Permanent public assets vector-tiles URL for the project (may be null). */
export const useAssetsTilesUrl = (
  projectId: string | undefined,
  enabled = true
) => {
  return useQuery<string | null, ApiError>({
    queryKey: ["storyAssetsTilesUrl", projectId],
    queryFn: async () => {
      try {
        const response = await api.request<
          ApiResponse<{ tiles_url: string | null }>
        >(`/api/projects/${projectId}/assets/tiles-url/`);
        return response.data?.tiles_url ?? null;
      } catch {
        return null;
      }
    },
    enabled: !!projectId && enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * Best-effort project fire-history GeoJSON for story scenes (builder/preview;
 * published stories carry it frozen in content.mapData.fireHistory).
 */
export const useFireHistory = (
  projectId: string | undefined,
  enabled = true
) => {
  return useQuery<GeoJSON.FeatureCollection | null, ApiError>({
    queryKey: ["storyFireHistory", projectId],
    queryFn: async () => {
      try {
        const response = await api.request<
          ApiResponse<GeoJSON.FeatureCollection>
        >(`/api/projects/${projectId}/fireHistory`);
        return response.data ?? null;
      } catch {
        return null;
      }
    },
    enabled: !!projectId && enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * Treatments per strategy for any number of layer entries, keyed by strategy
 * id (see useStrategyTreatments for the single-strategy variant). Missing
 * entries are still loading or failed — best-effort.
 */
export const useStrategyTreatmentsMap = (
  projectId: string | undefined,
  strategyIds: number[],
  enabled = true
): {
  treatmentsByStrategy: Record<string, GeoJSON.FeatureCollection>;
  isPending: boolean;
} => {
  return useQueries({
    queries: strategyIds.map((strategyId) => ({
      queryKey: ["storyMapTreatments", projectId, strategyId],
      queryFn: async (): Promise<GeoJSON.FeatureCollection | null> => {
        try {
          const response = await api.request<ApiResponse<GeoJSON.FeatureCollection>>(
            `/api/projects/${projectId}/mitigationStrategies/${strategyId}/treatments`
          );
          return response.data ?? null;
        } catch {
          return null;
        }
      },
      enabled: !!projectId && enabled,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const treatmentsByStrategy: Record<string, GeoJSON.FeatureCollection> = {};
      results.forEach((q, i) => {
        if (q.data) treatmentsByStrategy[String(strategyIds[i])] = q.data;
      });
      return {
        treatmentsByStrategy,
        isPending: enabled && results.some((q) => q.isPending),
      };
    },
  });
};

/**
 * Collated results per simulation for any number of sim-layer entries, keyed
 * by simulation id (see useStorySimResults for the single-sim variant).
 */
export const useStorySimResultsMap = (
  projectId: string | undefined,
  simulationIds: string[],
  enabled = true
): {
  resultsBySim: Record<string, unknown[]>;
  isPending: boolean;
} => {
  return useQueries({
    queries: simulationIds.map((simulationId) => ({
      queryKey: ["storySimResults", projectId, simulationId],
      queryFn: async (): Promise<unknown[] | null> => {
        try {
          const response = await api.request<ApiResponse<{ results: unknown[] }>>(
            `/api/projects/${projectId}/fireSimulationResult?simulation_id=${simulationId}`
          );
          return response.data?.results ?? null;
        } catch {
          return null;
        }
      },
      enabled: !!projectId && enabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const resultsBySim: Record<string, unknown[]> = {};
      results.forEach((q, i) => {
        if (q.data) resultsBySim[simulationIds[i]] = q.data;
      });
      return {
        resultsBySim,
        isPending: enabled && results.some((q) => q.isPending),
      };
    },
  });
};

/** One footprints request for story inspection entries (ensemble sims). */
export interface StoryInspectRequest {
  simulationId: string;
  /** Member index for percentile-scoped entries; null = across all members. */
  member: number | null;
}

export const storyInspectRequestKey = (request: StoryInspectRequest): string =>
  `${request.simulationId}:${request.member ?? "all"}`;

/** Wire shape of fire-simulation-inspect-footprints. */
export interface StoryInspectFootprints {
  mode: "member" | "confidence";
  points_per_member: number;
  members: number;
  footprints: {
    point: number;
    footprint: GeoJSON.FeatureCollection | null;
  }[];
}

/**
 * Per-base-point final fire footprints for ensemble inspection entries,
 * fetched ONCE per (simulation, view) — inspection-point hit-tests then run
 * locally, so moving the point never waits on a request. Published stories
 * carry the frozen per-entry visibility in mapData.inspection instead.
 */
export const useStoryInspectFootprints = (
  projectId: string | undefined,
  requests: StoryInspectRequest[],
  enabled = true
): { byRequest: Record<string, StoryInspectFootprints>; isPending: boolean } => {
  return useQueries({
    queries: requests.map((request) => ({
      queryKey: [
        "storyInspectFootprints",
        projectId,
        request.simulationId,
        request.member,
      ],
      queryFn: async (): Promise<StoryInspectFootprints | null> => {
        try {
          const response = await api.request<ApiResponse<StoryInspectFootprints>>(
            `/api/projects/${projectId}/fire-simulation-inspect-footprints` +
              `?simulation_id=${request.simulationId}` +
              (request.member !== null ? `&member=${request.member}` : "")
          );
          return response.data ?? null;
        } catch {
          return null;
        }
      },
      enabled: !!projectId && enabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const byRequest: Record<string, StoryInspectFootprints> = {};
      results.forEach((q, i) => {
        if (q.data) byRequest[storyInspectRequestKey(requests[i])] = q.data;
      });
      return {
        byRequest,
        isPending: enabled && results.some((q) => q.isPending),
      };
    },
  });
};

/**
 * Best-effort ignition regimes (with their ignition features) for story
 * scenes' "ignitions" layer. Published stories carry the cited regime frozen
 * in mapData.ignitionRegimes instead.
 */
export const useStoryIgnitionRegimes = (
  projectId: string | undefined,
  enabled = true
) => {
  return useQuery<IgnitionRegime[] | null, ApiError>({
    queryKey: ["storyIgnitionRegimes", projectId],
    queryFn: async () => {
      try {
        const response = await api.request<IgnitionRegime[]>(
          `/api/projects/${projectId}/ignition-regimes/`
        );
        // Tolerate either a bare list or a {data} envelope.
        const list = (response as unknown as { data?: IgnitionRegime[] })?.data ?? response;
        return Array.isArray(list) ? list : null;
      } catch {
        return null;
      }
    },
    enabled: !!projectId && enabled,
    refetchOnWindowFocus: false,
  });
};

/** A regime's ignition features as a FeatureCollection. */
export function regimeToFeatureCollection(
  regime: IgnitionRegime | null | undefined
): GeoJSON.FeatureCollection | null {
  if (!regime || !Array.isArray(regime.ignitions)) return null;
  return {
    type: "FeatureCollection",
    features: regime.ignitions.filter(
      (i) => !!i?.geometry
    ) as unknown as GeoJSON.Feature[],
  };
}

/**
 * Best-effort custom-layer list for story scenes' "custom_layers" layer
 * (picker labels + style metadata). Frozen equivalents live in
 * mapData.customLayers.
 */
export const useStoryCustomLayers = (
  projectId: string | undefined,
  enabled = true
) => {
  return useQuery<CustomLayer[] | null, ApiError>({
    queryKey: ["storyCustomLayers", projectId],
    queryFn: async () => {
      try {
        const response = await api.request<CustomLayer[]>(
          `/api/projects/${projectId}/custom_layers/`
        );
        const list = (response as unknown as { data?: CustomLayer[] })?.data ?? response;
        return Array.isArray(list) ? list : null;
      } catch {
        return null;
      }
    },
    enabled: !!projectId && enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * Features for the given custom layers, fetched concurrently and keyed by
 * layer id. Missing entries are still loading (or failed — best-effort).
 */
export const useStoryCustomLayerFeatures = (
  projectId: string | undefined,
  layerIds: number[],
  enabled = true
): { featuresById: Record<number, GeoJSON.FeatureCollection>; isPending: boolean } => {
  return useQueries({
    queries: layerIds.map((layerId) => ({
      queryKey: ["storyCustomLayerFeatures", projectId, layerId],
      queryFn: async (): Promise<GeoJSON.FeatureCollection | null> => {
        try {
          // The detail endpoint serialises `geometries` as a bare array of
          // Features (BaseGeoJSONSerializer, many=True) — NOT a
          // FeatureCollection. Normalise here so every consumer, and the
          // frozen mapData bundle, see the same shape.
          type Geometries = GeoJSON.Feature[] | GeoJSON.FeatureCollection;
          const response = await api.request<{
            geometries?: Geometries;
            data?: { geometries?: Geometries };
          }>(`/api/projects/${projectId}/custom_layers/${layerId}`);
          const geometries: Geometries | null =
            response?.data?.geometries ?? response?.geometries ?? null;
          if (!geometries) return null;
          return Array.isArray(geometries)
            ? { type: "FeatureCollection", features: geometries }
            : geometries;
        } catch {
          return null;
        }
      },
      enabled: !!projectId && enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false as const,
    })),
    // Structurally shared, so consumers can put featuresById in memo deps.
    combine: (results) => {
      const featuresById: Record<number, GeoJSON.FeatureCollection> = {};
      results.forEach((q, i) => {
        if (q.data) featuresById[layerIds[i]] = q.data;
      });
      return {
        featuresById,
        isPending: enabled && results.some((q) => q.isPending),
      };
    },
  });
};

// ---------------------------------------------------------------------------
// Per-project variants for folder and organisation-wide stories: the same
// queries (same keys, shared cache) fanned out over (project, id) requests,
// because a story's entries may cite several projects.
// ---------------------------------------------------------------------------

export type ProjectIdLike = number | string;
const pidKey = (projectId: ProjectIdLike) => String(projectId);

export const useStrategyTreatmentsByProject = (
  requests: { projectId: ProjectIdLike; strategyId: number }[],
  enabled = true
): { treatmentsByStrategy: Record<string, GeoJSON.FeatureCollection>; isPending: boolean } => {
  return useQueries({
    queries: requests.map(({ projectId, strategyId }) => ({
      queryKey: ["storyMapTreatments", pidKey(projectId), strategyId],
      queryFn: async (): Promise<GeoJSON.FeatureCollection | null> => {
        try {
          const response = await api.request<ApiResponse<GeoJSON.FeatureCollection>>(
            `/api/projects/${pidKey(projectId)}/mitigationStrategies/${strategyId}/treatments`
          );
          return response.data ?? null;
        } catch {
          return null;
        }
      },
      enabled,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const treatmentsByStrategy: Record<string, GeoJSON.FeatureCollection> = {};
      results.forEach((q, i) => {
        if (q.data) treatmentsByStrategy[String(requests[i].strategyId)] = q.data;
      });
      return { treatmentsByStrategy, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

export const useStorySimResultsByProject = (
  requests: { projectId: ProjectIdLike; simulationId: string }[],
  enabled = true
): { resultsBySim: Record<string, unknown[]>; isPending: boolean } => {
  return useQueries({
    queries: requests.map(({ projectId, simulationId }) => ({
      queryKey: ["storySimResults", pidKey(projectId), simulationId],
      queryFn: async (): Promise<unknown[] | null> => {
        try {
          const response = await api.request<ApiResponse<{ results: unknown[] }>>(
            `/api/projects/${pidKey(projectId)}/fireSimulationResult?simulation_id=${simulationId}`
          );
          return response.data?.results ?? null;
        } catch {
          return null;
        }
      },
      enabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const resultsBySim: Record<string, unknown[]> = {};
      results.forEach((q, i) => {
        if (q.data) resultsBySim[requests[i].simulationId] = q.data;
      });
      return { resultsBySim, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

export interface StoryInspectRequestFor extends StoryInspectRequest {
  projectId: ProjectIdLike;
}

export const useStoryInspectFootprintsByProject = (
  requests: StoryInspectRequestFor[],
  enabled = true
): { byRequest: Record<string, StoryInspectFootprints>; isPending: boolean } => {
  return useQueries({
    queries: requests.map((request) => ({
      queryKey: [
        "storyInspectFootprints",
        pidKey(request.projectId),
        request.simulationId,
        request.member,
      ],
      queryFn: async (): Promise<StoryInspectFootprints | null> => {
        try {
          const response = await api.request<ApiResponse<StoryInspectFootprints>>(
            `/api/projects/${pidKey(request.projectId)}/fire-simulation-inspect-footprints` +
              `?simulation_id=${request.simulationId}` +
              (request.member !== null ? `&member=${request.member}` : "")
          );
          return response.data ?? null;
        } catch {
          return null;
        }
      },
      enabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const byRequest: Record<string, StoryInspectFootprints> = {};
      results.forEach((q, i) => {
        if (q.data) byRequest[storyInspectRequestKey(requests[i])] = q.data;
      });
      return { byRequest, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

export const useStoryIgnitionRegimesByProject = (
  projectIds: ProjectIdLike[],
  enabled = true
): { regimesByProject: Record<string, IgnitionRegime[]>; isPending: boolean } => {
  return useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ["storyIgnitionRegimes", pidKey(projectId)],
      queryFn: async (): Promise<IgnitionRegime[] | null> => {
        try {
          const response = await api.request<IgnitionRegime[]>(
            `/api/projects/${pidKey(projectId)}/ignition-regimes/`
          );
          const list = (response as unknown as { data?: IgnitionRegime[] })?.data ?? response;
          return Array.isArray(list) ? list : null;
        } catch {
          return null;
        }
      },
      enabled,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const regimesByProject: Record<string, IgnitionRegime[]> = {};
      results.forEach((q, i) => {
        if (q.data) regimesByProject[pidKey(projectIds[i])] = q.data;
      });
      return { regimesByProject, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

export const useStoryCustomLayersByProject = (
  projectIds: ProjectIdLike[],
  enabled = true
): { layersByProject: Record<string, CustomLayer[]>; isPending: boolean } => {
  return useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ["storyCustomLayers", pidKey(projectId)],
      queryFn: async (): Promise<CustomLayer[] | null> => {
        try {
          const response = await api.request<CustomLayer[]>(
            `/api/projects/${pidKey(projectId)}/custom_layers/`
          );
          const list = (response as unknown as { data?: CustomLayer[] })?.data ?? response;
          return Array.isArray(list) ? list : null;
        } catch {
          return null;
        }
      },
      enabled,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const layersByProject: Record<string, CustomLayer[]> = {};
      results.forEach((q, i) => {
        if (q.data) layersByProject[pidKey(projectIds[i])] = q.data;
      });
      return { layersByProject, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

export const useStoryCustomLayerFeaturesByProject = (
  requests: { projectId: ProjectIdLike; layerId: number }[],
  enabled = true
): { featuresById: Record<number, GeoJSON.FeatureCollection>; isPending: boolean } => {
  return useQueries({
    queries: requests.map(({ projectId, layerId }) => ({
      queryKey: ["storyCustomLayerFeatures", pidKey(projectId), layerId],
      queryFn: async (): Promise<GeoJSON.FeatureCollection | null> => {
        try {
          type Geometries = GeoJSON.Feature[] | GeoJSON.FeatureCollection;
          const response = await api.request<{
            geometries?: Geometries;
            data?: { geometries?: Geometries };
          }>(`/api/projects/${pidKey(projectId)}/custom_layers/${layerId}`);
          const geometries: Geometries | null =
            response?.data?.geometries ?? response?.geometries ?? null;
          if (!geometries) return null;
          return Array.isArray(geometries)
            ? { type: "FeatureCollection", features: geometries }
            : geometries;
        } catch {
          return null;
        }
      },
      enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const featuresById: Record<number, GeoJSON.FeatureCollection> = {};
      results.forEach((q, i) => {
        if (q.data) featuresById[requests[i].layerId] = q.data;
      });
      return { featuresById, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

export const useFireHistoryByProject = (
  projectIds: ProjectIdLike[],
  enabled = true
): { byProject: Record<string, GeoJSON.FeatureCollection | null>; isPending: boolean } => {
  return useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ["storyFireHistory", pidKey(projectId)],
      queryFn: async (): Promise<GeoJSON.FeatureCollection | null> => {
        try {
          const response = await api.request<ApiResponse<GeoJSON.FeatureCollection>>(
            `/api/projects/${pidKey(projectId)}/fireHistory`
          );
          return response.data ?? null;
        } catch {
          return null;
        }
      },
      enabled,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const byProject: Record<string, GeoJSON.FeatureCollection | null> = {};
      results.forEach((q, i) => {
        if (q.data !== undefined) byProject[pidKey(projectIds[i])] = q.data;
      });
      return { byProject, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

export const useAssetsTilesUrlByProject = (
  projectIds: ProjectIdLike[],
  enabled = true
): { byProject: Record<string, string | null>; isPending: boolean } => {
  return useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ["storyAssetsTilesUrl", pidKey(projectId)],
      queryFn: async (): Promise<string | null> => {
        try {
          const response = await api.request<ApiResponse<{ tiles_url: string | null }>>(
            `/api/projects/${pidKey(projectId)}/assets/tiles-url/`
          );
          return response.data?.tiles_url ?? null;
        } catch {
          return null;
        }
      },
      enabled,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const byProject: Record<string, string | null> = {};
      results.forEach((q, i) => {
        if (q.data !== undefined) byProject[pidKey(projectIds[i])] = q.data;
      });
      return { byProject, isPending: enabled && results.some((q) => q.isPending) };
    },
  });
};

/**
 * Best-effort collated results for a scene's simulation, powering the
 * Risk from / Risk to / Inspection story layers (builder/preview). Published
 * stories carry a compact frozen slice in mapData.simLayers instead.
 */
export const useStorySimResults = (
  projectId: string | undefined,
  simulationId: string | null | undefined,
  enabled = true
) => {
  return useQuery<unknown[] | null, ApiError>({
    queryKey: ["storySimResults", projectId, simulationId],
    queryFn: async () => {
      try {
        const response = await api.request<
          ApiResponse<{ results: unknown[] }>
        >(
          `/api/projects/${projectId}/fireSimulationResult?simulation_id=${simulationId}`
        );
        return response.data?.results ?? null;
      } catch {
        return null;
      }
    },
    enabled: !!projectId && !!simulationId && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Wire shape of the frozen public JSON that stories published before frame
 * packs still point at (the old base64 body of fire-simulation-frame-arrays).
 */
interface RawStoryFrameArrays extends UrbanAnimationFields {
  shape: [number, number];
  bounds: RasterBounds;
  frame_times_seconds: number[];
  arrival: string;
  ros_at_ignition: string;
  intensity_at_ignition: string;
  flame_height_at_ignition: string;
  max_ros: number;
  max_intensity: number;
  max_flame_height: number;
  max_hour?: number;
}

function decodeStoryFrameArrays(r: RawStoryFrameArrays): FrameArrays {
  return {
    shape: r.shape,
    bounds: r.bounds,
    frameTimesSeconds: r.frame_times_seconds,
    arrival: b64ToF32(r.arrival),
    rosAtIgnition: b64ToF32(r.ros_at_ignition),
    intensityAtIgnition: b64ToF32(r.intensity_at_ignition),
    flameHeightAtIgnition: b64ToF32(r.flame_height_at_ignition),
    maxRos: r.max_ros,
    maxIntensity: r.max_intensity,
    maxFlameHeight: r.max_flame_height,
    maxHour: r.max_hour ?? 0,
    // Urban structure timings ride along on both the authed endpoints and the
    // frozen public JSONs (the publish freeze has always included them) —
    // they drive the burning/burnt building overlay during scene playback.
    urban: pickUrban(r),
  };
}

/** Fetch a frozen public JSON (tolerates a bare payload or {data} envelope). */
async function fetchPublicJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error("Failed to load animation data.");
  }
  const json = await response.json();
  return (json?.data ?? json) as T;
}

/** Fetch a frozen public frame pack (`.fsp`, stored gzip-encoded). */
async function fetchPublicPack(url: string, signal?: AbortSignal): Promise<FramePack> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error("Failed to load animation data.");
  }
  return decodeFramePack(await response.arrayBuffer());
}

/**
 * Data one animation entry's query resolves to: timed frame arrays (views
 * "single"/"percentile") or the static burn-probability stack ("confidence").
 */
export type StoryAnimationData =
  | { kind: "frames"; frames: FrameArrays }
  | { kind: "confidence"; confidence: ConfidenceArrays };

/** Inputs storyAnimationQueryOptions needs from a scene animation entry. */
export interface StoryAnimationQueryArgs {
  projectId: string | undefined;
  simulationId: string | null;
  view: "single" | "percentile" | "confidence";
  partition: string | number | null;
  resolvedMember: number | null;
  /**
   * The entry's percentile. A percentile entry whose member was never
   * resolved (agent-written drafts: only the builder's settings panel writes
   * resolvedMember) resolves it here, so preview doesn't wait forever.
   */
  percentile?: number | null;
  /**
   * Frozen public frame data (public viewer) — bypasses the authed endpoints.
   * A frame pack (`.fsp`), or the legacy base64 JSON for older publishes.
   */
  publicUrl: string | null;
  enabled: boolean;
}

/**
 * Query options for one scene-animation entry, shaped for useQueries (the
 * multi-entry animation hook maps scene.animations over this). Builder/preview
 * fetch the authed endpoint for the entry's view; the public viewer passes
 * `publicUrl` (the entry's frozen.framesUrl — the same frame pack, plain
 * fetch, no credentials).
 */
export function storyAnimationQueryOptions({
  projectId,
  simulationId,
  view,
  partition,
  resolvedMember,
  percentile = null,
  publicUrl,
  enabled,
}: StoryAnimationQueryArgs) {
  const isConfidence = view === "confidence";
  const resolveInQuery =
    view === "percentile" && resolvedMember == null && percentile != null;
  const paramsReady = publicUrl
    ? true
    : !!projectId &&
      !!simulationId &&
      (view === "single"
        ? partition != null
        : view === "percentile"
        ? resolvedMember != null || resolveInQuery
        : true);

  // Ensemble views: an entry may be scoped to one base ignition point via its
  // picked partition (the backend maps partition → point); null = whole scope.
  const scopeParam =
    partition != null ? `&partition=${encodeURIComponent(String(partition))}` : "";

  const queryKey = isConfidence
    ? ["storyConfidenceArrays", projectId, simulationId, partition, publicUrl]
    : view === "percentile"
    ? [
        "storyMemberArrays",
        projectId,
        simulationId,
        resolveInQuery ? `p${percentile}` : resolvedMember,
        partition,
        publicUrl,
      ]
    : ["storyFrameArrays", projectId, simulationId, partition, publicUrl];

  return {
    queryKey,
    queryFn: async ({
      signal,
    }: {
      signal?: AbortSignal;
    }): Promise<StoryAnimationData> => {
      // Stories published before frame packs point at base64 JSON.
      if (publicUrl && !isFramePackUrl(publicUrl)) {
        return isConfidence
          ? {
              kind: "confidence",
              confidence: decodeStoryConfidenceArrays(
                await fetchPublicJson<RawStoryConfidenceArrays>(publicUrl, signal)
              ),
            }
          : {
              kind: "frames",
              frames: decodeStoryFrameArrays(
                await fetchPublicJson<RawStoryFrameArrays>(publicUrl, signal)
              ),
            };
      }
      const member =
        resolveInQuery && !publicUrl
          ? (
              await api.request<ApiResponse<PercentileMemberData>>(
                `/api/projects/${projectId}/fire-simulation-percentile-member?simulation_id=${simulationId}&percentile=${percentile}`,
                { signal }
              )
            ).data.member
          : resolvedMember;
      const url = isConfidence
        ? `/api/projects/${projectId}/fire-simulation-ensemble-confidence?simulation_id=${simulationId}${scopeParam}`
        : view === "percentile"
        ? `/api/projects/${projectId}/fire-simulation-member-arrays?simulation_id=${simulationId}&member=${member}${scopeParam}`
        : `/api/projects/${projectId}/fire-simulation-frame-arrays?simulation_id=${simulationId}&partition=${partition}`;
      const pack = publicUrl
        ? await fetchPublicPack(publicUrl, signal)
        : await fetchFramePack(url, signal);
      return isConfidence
        ? { kind: "confidence", confidence: confidenceArraysFromPack(pack) }
        : { kind: "frames", frames: frameArraysFromPack(pack) };
    },
    enabled: enabled && paramsReady,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  } as const;
}

/** Wire shape of a legacy frozen confidence JSON (see RawStoryFrameArrays). */
interface RawStoryConfidenceArrays {
  shape: [number, number];
  bounds: RasterBounds;
  frame_times_seconds: number[];
  members: number;
  frame_count: number;
  burn_prob: string;
}

function decodeStoryConfidenceArrays(
  r: RawStoryConfidenceArrays
): ConfidenceArrays {
  return {
    shape: r.shape,
    bounds: r.bounds,
    frameTimesSeconds: r.frame_times_seconds,
    members: r.members,
    frameCount: r.frame_count,
    burnProb: b64ToU8(r.burn_prob),
  };
}

/** Response of fire-simulation-percentile-member. */
export interface PercentileMemberData {
  member: number;
  burned_area_ha: number | null;
}

/**
 * Resolve which ensemble member represents the N-th percentile of the final
 * burned-area distribution (builder, view "percentile"). The resolved index is
 * cached into animation.resolvedMember so preview doesn't re-resolve.
 */
export const usePercentileMember = (
  projectId: string | undefined,
  simulationId: string | null | undefined,
  percentile: number | null | undefined,
  enabled = true
) => {
  return useQuery<PercentileMemberData, ApiError>({
    queryKey: ["storyPercentileMember", projectId, simulationId, percentile],
    queryFn: async () => {
      const response = await api.request<ApiResponse<PercentileMemberData>>(
        `/api/projects/${projectId}/fire-simulation-percentile-member?simulation_id=${simulationId}&percentile=${percentile}`
      );
      return response.data;
    },
    enabled: !!projectId && !!simulationId && percentile != null && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Upload an image (data URL, png/jpeg/webp ≤10MB) to the story's media store.
 * Returns the permanent public S3 URL to store on the image block.
 */
export const useUploadStoryMedia = (storyMapId: string | undefined) => {
  return useMutation<ApiResponse<{ url: string }>, ApiError, string>({
    mutationFn: (imageData) =>
      api.request<ApiResponse<{ url: string }>>(storyPath(storyMapId, "/media"), {
        method: "POST",
        body: { image_data: imageData },
      }),
  });
};
